import React, { Component, Fragment } from 'react';

import classNames from 'classnames';
import { Row, Col } from 'reactstrap';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';

import { COLLAPSE_STATE, USER_INFO_POSITION } from 'site-modules/shared/constants/lead-form/lead-form-params';
import { getLeadUid } from 'site-modules/shared/components/lead-form/utils/utils';
import {
  LeadFormCommonPropTypes,
  LeadFormCommonDefaultProps,
  UnifiedLeadFormCustomConfigPropTypes,
  UnifiedLeadFormCustomConfigDefaultProps,
} from 'site-modules/shared/components/lead-form/lead-form-prop-types/lead-form-prop-types';

import { FormViewDecorator } from 'site-modules/shared/components/lead-form/unified-lead-form/views/form-view-decorator';
import { UserInfo } from 'site-modules/shared/components/lead-form/unified-lead-form/components/user-info/user-info';
import { CardList } from 'site-modules/shared/components/lead-form/unified-lead-form/components/cards/card-list';
import { Card } from 'site-modules/shared/components/lead-form/unified-lead-form/components/cards/card';
import { Link } from 'site-modules/shared/components/link/link';

const DEFAULT_COL_SIZE = { xs: 12, md: 6 };
const classNameCardText = 'text-gray-darker fw-medium mb-0_75';

class BaseFormViewUI extends Component {
  static propTypes = {
    ...LeadFormCommonPropTypes,
    customConfig: UnifiedLeadFormCustomConfigPropTypes.leadFormConfig,
    profile: PropTypes.shape({}),
    pageName: PropTypes.string,
    isMobile: PropTypes.bool,
  };

  static defaultProps = {
    ...LeadFormCommonDefaultProps,
    customConfig: {
      ...UnifiedLeadFormCustomConfigDefaultProps.leadFormConfig,
    },
    profile: {},
    pageName: null,
    isMobile: false,
  };

  /**
   * Returns value that indicates if we have a possibility to show a checkbox.
   * In case if we can show checkbox, fires a tracking event once.
   * @return {boolean}
   */
  showCheckBox = () => {
    const {
      customConfig: { checkbox },
      pageName,
      leadTargets,
    } = this.props;

    const isVisible = !!checkbox.isVisible({ leadTargets, pageName });

    if (!this.checkboxDisplayed && isVisible) {
      this.checkboxDisplayed = true;
      checkbox.fireIsVisibleTracking(isVisible);
    }

    return isVisible;
  };

  renderCardFirstText = showBreakpoint => {
    const {
      customConfig: { cardFirstText },
    } = this.props;
    return (
      <Col xs={12} md={6} className={classNames(classNameCardText, 'mt-0_75', showBreakpoint)}>
        {cardFirstText}
      </Col>
    );
  };

  renderCard = (leadTarget, index, multi) => {
    const {
      onToggleLeadSelection,
      selectedLeadIds,
      customConfig: {
        cardSecondText,
        priceStrikethrough,
        showInventoryMakeStockImage,
        showDealerMakeStockImage,
        showBestDealPrice,
        userInfoPosition,
        cardView,
        inventoryText,
        reverseCardView,
        listCardView,
        explainingTextParams,
        showTeaserText,
      },
    } = this.props;
    const key = getLeadUid(leadTarget);
    const showUserInfo = index + 1 === userInfoPosition;
    return (
      <Fragment key={key}>
        {index === 1 && cardSecondText && <div className={classNameCardText}>{cardSecondText}</div>}
        <Card
          classes={classNames('rounded mb-0_5', {
            'd-flex ps-1 pe-md-1': reverseCardView,
            'ps-1 pe-2': !reverseCardView,
            'border-0': listCardView,
            'py-1': !listCardView,
          })}
          slot={index}
          leadTarget={leadTarget}
          onChangeLeadSelection={onToggleLeadSelection}
          selectedLeadIds={selectedLeadIds}
          multi={multi}
          priceStrikethrough={priceStrikethrough}
          showInventoryMakeStockImage={showInventoryMakeStockImage}
          showDealerMakeStockImage={showDealerMakeStockImage}
          showBestDealPrice={showBestDealPrice}
          cardView={cardView}
          inventoryText={inventoryText}
          reverseCardView={reverseCardView}
          explainingTextParams={explainingTextParams}
          showTeaserText={showTeaserText}
        />
        {showUserInfo && this.renderUserInfo()}
      </Fragment>
    );
  };

  renderCards = () => {
    const {
      leadTargets,
      showListTargetsLoadingIndicator,
      selectedLeadIds,
      fieldRef,
      validationErrors,
      customConfig: { options },
    } = this.props;
    const cardListProps = { selectedLeadIds, fieldRef, validationErrors, showListTargetsLoadingIndicator };
    const colSize = get(options, 'layoutOptions.cardListColSize') || DEFAULT_COL_SIZE;

    return (
      <Col {...colSize}>
        <CardList {...cardListProps}>
          {leadTargets.map((leadTarget, index) => this.renderCard(leadTarget, index, leadTargets.length > 1))}
        </CardList>
      </Col>
    );
  };

  renderUserInfoCol = () => {
    const {
      customConfig: { options },
    } = this.props;
    const colSize = get(options, 'layoutOptions.userInfoColSize') || DEFAULT_COL_SIZE;
    return <Col {...colSize}>{this.renderUserInfo()}</Col>;
  };

  renderUserInfo = () => {
    const {
      leadTargets,
      showSubmitLoadingIndicator,
      fieldRef,
      validationErrors,
      validate,
      profile,
      customConfig: {
        enableComments,
        applyPhoneMask,
        submitButtonText,
        toggleableSubmitInfo,
        checkbox,
        tcpaParentDealershipName,
        initialCollapseState,
        userInfoOptions,
        type,
      },
      resetFieldValidation,
      make,
    } = this.props;

    const [oneDealerSubmitText, multiDealerSubmitText] = submitButtonText.split('|');

    return (
      <UserInfo
        fieldRef={fieldRef}
        validate={validate}
        validationErrors={validationErrors}
        submitButtonText={
          get(leadTargets, 'length', 1) > 1 ? multiDealerSubmitText || oneDealerSubmitText : oneDealerSubmitText
        }
        enableComments={enableComments}
        applyPhoneMask={applyPhoneMask}
        showSubmitLoadingIndicator={showSubmitLoadingIndicator}
        toggleableSubmitInfo={toggleableSubmitInfo}
        profile={profile}
        showCheckbox={this.showCheckBox}
        checkbox={checkbox}
        nextButtonTrackingId={'next_user_info_click'}
        initialCollapseState={initialCollapseState || COLLAPSE_STATE.PARTIAL_COLLAPSE}
        tcpaParentDealershipName={tcpaParentDealershipName}
        userInfoOptions={userInfoOptions}
        resetFieldValidation={resetFieldValidation}
        isSingleSelect={leadTargets.length <= 1}
        make={make}
        type={type}
      />
    );
  };

  render() {
    const {
      customConfig: { cardFirstText, userInfoPosition, disableInventoryCard, toggleableSubmitInfo, srpLink },
    } = this.props;
    const showCardFirstText = !disableInventoryCard && cardFirstText;

    return (
      <Fragment>
        {showCardFirstText && (
          <Row>
            {userInfoPosition === USER_INFO_POSITION.BEFORE_CARDS && <Col xs={12} md={6} />}
            {this.renderCardFirstText('hidden-md-down')}
            {userInfoPosition === USER_INFO_POSITION.AFTER_CARDS && <Col xs={12} md={6} />}
          </Row>
        )}
        <Row className={classNames({ 'mt-md-2': !showCardFirstText }, { 'mt-0': showCardFirstText })}>
          {userInfoPosition === USER_INFO_POSITION.BEFORE_CARDS && this.renderUserInfoCol()}
          {showCardFirstText && this.renderCardFirstText('hidden-md-up')}
          {!disableInventoryCard && this.renderCards()}
          {userInfoPosition === USER_INFO_POSITION.AFTER_CARDS && this.renderUserInfoCol()}
        </Row>
        {srpLink && toggleableSubmitInfo && (
          <Row>
            <Col xs={12}>
              <Link
                to={srpLink.to}
                className="btn btn-outline-primary-b size-14 btn-sm mt-1"
                data-tracking-id="view_search_result"
                rel="nofollow"
              >
                {srpLink.text}
              </Link>
            </Col>
          </Row>
        )}
        {srpLink && !toggleableSubmitInfo && (
          <Row>
            <Col xs={12} className="text-end">
              <Link to={srpLink.to} className="text-primary mt-1" data-tracking-id="view_search_result" rel="nofollow">
                {srpLink.text} <i className="ms-0_25 icon icon-arrow-right4 small" aria-hidden />
              </Link>
            </Col>
          </Row>
        )}
      </Fragment>
    );
  }
}

export const BaseFormView = FormViewDecorator(BaseFormViewUI);
